import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  CircularProgress,
  useMediaQuery,
  Stack,
  Chip,
  Divider,
  Tooltip as MuiTooltip,
  Skeleton,
  Button,
  Paper,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
} from "@mui/material";
import { tokens } from "../../../theme";
import {
  InfoOutlined,
  BarChart as BarChartIcon,
  Download,
  TableChart,
  CompareArrows,
  AttachMoney,
  PriceCheck,
  CalendarMonth,
   ErrorOutline,
} from "@mui/icons-material";

const PurchasePriceVariance = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const token = localStorage.getItem("token");
  const cmpid = localStorage.getItem("cmpid");

  // State management
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("table");
  const [isUsingSampleData, setIsUsingSampleData] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [availableMonths, setAvailableMonths] = useState([]);

  // Stats state
  const [stats, setStats] = useState({
    totalVariance: 0,
    avgVariance: 0,
    positiveVarianceCount: 0,
    negativeVarianceCount: 0,
    highestVariance: { product: "", amount: 0 },
    lowestVariance: { product: "", amount: 0 },
    totalSavings: 0,
    totalOverSpend: 0,
  });

  // Configuration constants
  const CONFIG = {
    chart: {
      barSize: 30,
      colors: {
        currentRate: colors.redAccent[500],
        lastRate: colors.blueAccent[500],
        variance: colors.greenAccent[500],
      },
    },
    colors: {
      primary: [colors.blueAccent[500], colors.greenAccent[500]],
      highlight: colors.blueAccent[700],
      tooltipBg: theme.palette.background.paper,
      cardBg: theme.palette.background.default,
      success: colors.greenAccent[500],
      warning: colors.redAccent[500],
      textPrimary: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary,
    },
  };

  // Generate sample data for demo purposes
  const generateSampleData = () => {
    const products = [
      "Set wiper bl - 61615A974B5",
      "Gadget B",
      "Tool C",
      "Part D",
      "Material E",
      "Component F",
      "Device G",
      "Accessory H",
    ];

    const months = ["2025-06", "2025-07", "2025-08"];

    return products.map((product) => {
      const lastRate = Math.random() * 10000 + 1000;
      const currentRate = lastRate * (0.8 + Math.random() * 0.4);
      const variance = currentRate - lastRate;
      const month = months[Math.floor(Math.random() * months.length)];

      return {
        id: Math.floor(Math.random() * 10000),
        product,
        lastRate: lastRate || 0,
        currentRate: currentRate || 0,
        variance: variance || 0,
        variancePercentage: lastRate !== 0 ? (variance / lastRate) * 100 : 0,
        month,
      };
    });
  };

  // Transform API data to match our expected format
  const transformApiData = (apiData) => {
    try {
      if (!apiData || typeof apiData !== "object") {
        return [];
      }

      // Convert object to array if needed
      const dataArray = Array.isArray(apiData)
        ? apiData
        : Object.values(apiData);

      const transformedData = dataArray.map((item) => {
        // Parse variance percentage from string if it exists
        let variancePercentage = 0;
        if (
          item.difference_percent &&
          typeof item.difference_percent === "string"
        ) {
          variancePercentage =
            parseFloat(item.difference_percent.replace("%", "")) || 0;
        }

        // Calculate variance amount
        const lastRate = item.last_rate || 0;
        const currentRate = item.current_rate || 0;
        const variance = currentRate - lastRate;

        return {
          id: `${item.stock_item}-${item.month}`,
          product: item.stock_item || "Unknown Product",
          lastRate,
          currentRate,
          variance,
          variancePercentage,
          month: item.month || "Unknown Month",
        };
      });

      return transformedData;
    } catch (error) {
      return [];
    }
  };

  // Extract unique months from data
  const extractMonths = (data) => {
    const months = new Set();
    data.forEach((item) => {
      if (item.month) {
        months.add(item.month);
      }
    });
    return Array.from(months).sort();
  };

  // Filter data by selected month
  const filterDataByMonth = (data, month) => {
    if (!month) return data;
    return data.filter((item) => item.month === month);
  };

  // Calculate statistics from data with safe defaults
  const calculateStats = (data) => {
    if (!data || data.length === 0)
      return {
        totalVariance: 0,
        avgVariance: 0,
        positiveVarianceCount: 0,
        negativeVarianceCount: 0,
        highestVariance: { product: "N/A", amount: 0 },
        lowestVariance: { product: "N/A", amount: 0 },
        totalSavings: 0,
        totalOverSpend: 0,
      };

    const variances = data.map((item) => item.variance || 0);
    const totalVariance = variances.reduce((sum, val) => sum + val, 0);
    const positiveVariances = variances.filter((v) => v > 0);
    const negativeVariances = variances.filter((v) => v < 0);

    const highestVarianceItem = data.reduce(
      (max, item) =>
        (item.variance || 0) > max.amount
          ? { product: item.product || "N/A", amount: item.variance || 0 }
          : max,
      { product: "", amount: -Infinity }
    );

    const lowestVarianceItem = data.reduce(
      (min, item) =>
        (item.variance || 0) < min.amount
          ? { product: item.product || "N/A", amount: item.variance || 0 }
          : min,
      { product: "", amount: Infinity }
    );

    return {
      totalVariance: totalVariance || 0,
      avgVariance: data.length > 0 ? totalVariance / data.length || 0 : 0,
      positiveVarianceCount: positiveVariances.length || 0,
      negativeVarianceCount: negativeVariances.length || 0,
      highestVariance: highestVarianceItem,
      lowestVariance: lowestVarianceItem,
      totalSavings:
        Math.abs(negativeVariances.reduce((sum, val) => sum + val, 0)) || 0,
      totalOverSpend: positiveVariances.reduce((sum, val) => sum + val, 0) || 0,
    };
  };

  // Enhanced fetch function with better error handling
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      setIsUsingSampleData(false);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/reports/purchasePriceVariance`,
        { cmpid },
        { headers: { Authorization: token } }
      );

      // Transform whatever data we get from the API
      const transformedData = transformApiData(response.data);

      // Check if we have valid data
      if (transformedData && transformedData.length > 0) {
        setData(transformedData);

        // Extract and set available months
        const months = extractMonths(transformedData);
        setAvailableMonths(months);

        // Set initial month selection (latest month)
        if (months.length > 0) {
          setSelectedMonth(months[months.length - 1]);
          setFilteredData(
            filterDataByMonth(transformedData, months[months.length - 1])
          );
        } else {
          setFilteredData(transformedData);
        }

        setStats(calculateStats(transformedData));
      } else {
        // If API returns empty data, use sample data
        setError("No data available. Showing sample data.");
        const sampleData = generateSampleData();
        setData(sampleData);

        // Extract and set available months for sample data
        const months = extractMonths(sampleData);
        setAvailableMonths(months);

        // Set initial month selection for sample data
        if (months.length > 0) {
          setSelectedMonth(months[0]);
          setFilteredData(filterDataByMonth(sampleData, months[0]));
        } else {
          setFilteredData(sampleData);
        }

        setStats(calculateStats(sampleData));
        setIsUsingSampleData(true);
      }
    } catch (err) {
      let errorMessage = "Failed to load data";
      if (err.response) {
        if (err.response.status === 401) {
          errorMessage = "Authentication failed - please login again";
        } else if (err.response.status === 404) {
          errorMessage = "Data not available";
        } else if (err.response.data && err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      } else if (err.message.includes("timeout")) {
        errorMessage = "Request timeout - server not responding";
      } else if (err.message.includes("Network Error")) {
        errorMessage = "Network error - please check your connection";
      }

      setError(`${errorMessage}. Showing sample data.`);
      const sampleData = generateSampleData();
      setData(sampleData);

      // Extract and set available months for sample data
      const months = extractMonths(sampleData);
      setAvailableMonths(months);

      // Set initial month selection for sample data
      if (months.length > 0) {
        setSelectedMonth(months[0]);
        setFilteredData(filterDataByMonth(sampleData, months[0]));
      } else {
        setFilteredData(sampleData);
      }

      setStats(calculateStats(sampleData));
      setIsUsingSampleData(true);
    } finally {
      setLoading(false);
    }
  };

  // Handle month selection change
  const handleMonthChange = (event) => {
    const month = event.target.value;
    setSelectedMonth(month);
    setFilteredData(filterDataByMonth(data, month));
    setStats(calculateStats(filterDataByMonth(data, month)));
  };

  // Load data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const dataItem = payload[0].payload;
      const lastRate = dataItem.lastRate || 0;
      const currentRate = dataItem.currentRate || 0;
      const variance = currentRate - lastRate;
      const variancePercentage =
        lastRate !== 0 ? (variance / lastRate) * 100 : 0;

      return (
        <Card
          sx={{
            backgroundColor: CONFIG.colors.tooltipBg,
            boxShadow: theme.shadows[3],
            borderRadius: 2,
            p: 2,
            minWidth: 220,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {dataItem.product || "N/A"}
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Stack spacing={1}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center" gap={0.5}>
                <PriceCheck fontSize="small" color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Last Rate:
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600}>
                Rs. {lastRate.toFixed(2)}
              </Typography>
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center" gap={0.5}>
                <AttachMoney fontSize="small" color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Current Rate:
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600}>
                Rs. {currentRate.toFixed(2)}
              </Typography>
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center" gap={0.5}>
                <CompareArrows
                  fontSize="small"
                  color={variance >= 0 ? "error" : "success"}
                />
                <Typography variant="body2" color="text.secondary">
                  Variance:
                </Typography>
              </Box>
              <Typography
                variant="body2"
                fontWeight={600}
                color={
                  variance >= 0
                    ? colors.redAccent[500]
                    : colors.greenAccent[500]
                }
              >
                Rs. {variance.toFixed(2)} ({variancePercentage.toFixed(1)}%)
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Month:
              </Typography>
              <Typography variant="body2">{dataItem.month || "N/A"}</Typography>
            </Box>
          </Stack>
        </Card>
      );
    }
    return null;
  };

  // Prepare chart data with safe defaults
  const chartData = filteredData
    .map((item) => ({
      ...item,
      lastRate: item.lastRate || 0,
      currentRate: item.currentRate || 0,
      variance: item.variance || 0,
      variancePercentage: item.variancePercentage || 0,
    }))
    .sort((a, b) => (b.variance || 0) - (a.variance || 0));

  // Export data to CSV
  const exportToCSV = () => {
    const headers = [
      "Stock Item Name",
      "Last Rate",
      "Current Rate",
      "Variance",
      "Variance %",
      "Month",
    ];
    const rows = chartData.map((item) => [
      item.product || "N/A",
      (item.lastRate || 0).toFixed(2),
      (item.currentRate || 0).toFixed(2),
      (item.variance || 0).toFixed(2),
      (item.variancePercentage || 0).toFixed(1),
      item.month || "N/A",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `purchase_variance_${selectedMonth || "all"}_${new Date()
        .toISOString()
        .slice(0, 10)}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Card
      sx={{
        height: "100%",
        minHeight: 450,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[2],
        borderRadius: "12px",
        overflow: "hidden",
        border: `1px solid ${theme.palette.divider}`,
        display: "flex",
        flexDirection: "column",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: theme.shadows[6],
        },
      }}
    >
      <CardContent
        sx={{
          p: isMobile ? 2 : 3,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Header Section */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: CONFIG.colors.textPrimary,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Avatar
                sx={{
                  bgcolor: colors.redAccent[500],
                  width: 32,
                  height: 32,
                  boxShadow: theme.shadows[2],
                }}
              >
                <PriceCheck fontSize="small" />
              </Avatar>
              Purchase Price Variance
              <MuiTooltip title="Comparison of current purchase prices vs. last rates">
                <InfoOutlined
                  sx={{
                    fontSize: 18,
                    color: CONFIG.colors.textSecondary,
                    cursor: "pointer",
                  }}
                />
              </MuiTooltip>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Current vs. last rate analysis
              {isUsingSampleData && " (Sample Data)"}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Button
              size="small"
              onClick={() => setViewMode(viewMode === "bar" ? "table" : "bar")}
              startIcon={viewMode === "bar" ? <TableChart /> : <BarChartIcon />}
              sx={{
                textTransform: "none",
                color: CONFIG.colors.textSecondary,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              {viewMode === "bar" ? "Table" : "Chart"}
            </Button>
            <Button
              size="small"
              onClick={exportToCSV}
              startIcon={<Download />}
              disabled={loading}
              sx={{
                textTransform: "none",
                color: CONFIG.colors.textSecondary,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              Export
            </Button>
          </Box>
        </Box>

        {/* Error/Sample Data Notice */}
        {error && (
          <Box>
            <Chip
              label={error}
              color="warning"
              size="small"
              variant="outlined"
              icon={<ErrorOutline fontSize="small" />}
              sx={{
                mb: 0.5,
                borderColor: colors.warning,
                color: colors.warning,
              }}
            />
            {isUsingSampleData && (
              <Typography variant="caption" color="text.secondary">
                Sample data may not reflect actual business metrics
              </Typography>
            )}
          </Box>
        )}

        {/* Month Selection */}
        <Box sx={{ minWidth: 200, maxWidth: 300 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="month-select-label">Select Month</InputLabel>
            <Select
              labelId="month-select-label"
              id="month-select"
              value={selectedMonth}
              label="Select Month"
              onChange={handleMonthChange}
              disabled={loading || availableMonths.length === 0}
              startAdornment={
                <CalendarMonth
                  sx={{ mr: 1, color: theme.palette.text.secondary }}
                />
              }
            >
              {availableMonths.map((month) => (
                <MenuItem key={month} value={month}>
                  {month}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Stats Summary */}
        <Box display="flex" flexDirection="column" gap={2}>
          {loading ? (
            <Skeleton variant="rounded" width="100%" height={120} />
          ) : (
            <Box
              display="grid"
              gridTemplateColumns={isMobile ? "1fr" : "repeat(3, 1fr)"}
              gap={2}
            >
              {/* Total Variance Card */}
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: CONFIG.colors.cardBg,
                  border: `1px solid ${theme.palette.divider}`,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Total Variance
                </Typography>
                <Typography variant="h5" fontWeight={700} mt={0.5}>
                  Rs. {(stats.totalVariance || 0).toFixed(2)}
                </Typography>
                <Typography variant="caption" color="text.secondary" mt={0.5}>
                  {stats.totalVariance >= 0 ? "Over budget" : "Under budget"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Avg: Rs. {(stats.avgVariance || 0).toFixed(2)} per item
                </Typography>
              </Paper>

              {/* Savings Card */}
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: CONFIG.colors.cardBg,
                  border: `1px solid ${theme.palette.divider}`,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Total Savings
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  mt={0.5}
                  sx={{
                    color: colors.greenAccent[500],
                  }}
                >
                  Rs. {(stats.totalSavings || 0).toFixed(2)}
                </Typography>
                <Typography variant="caption" color="text.secondary" mt={0.5}>
                  {stats.negativeVarianceCount || 0} items under budget
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Lowest: Rs. {(stats.lowestVariance.amount || 0).toFixed(2)} (
                  {stats.lowestVariance.product || "N/A"})
                </Typography>
              </Paper>

              {/* Overspend Card */}
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: CONFIG.colors.cardBg,
                  border: `1px solid ${theme.palette.divider}`,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Total Overspend
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  mt={0.5}
                  sx={{
                    color: colors.redAccent[500],
                  }}
                >
                  Rs. {(stats.totalOverSpend || 0).toFixed(2)}
                </Typography>
                <Typography variant="caption" color="text.secondary" mt={0.5}>
                  {stats.positiveVarianceCount || 0} items over budget
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Highest: Rs. {(stats.highestVariance.amount || 0).toFixed(2)}{" "}
                  ({stats.highestVariance.product || "N/A"})
                </Typography>
              </Paper>
            </Box>
          )}
        </Box>

        {/* Chart/Table Area */}
        <Box flex={1} minHeight={250} position="relative" mt={2}>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
              bgcolor={theme.palette.action.hover}
              borderRadius={2}
            >
              <CircularProgress size={isMobile ? 40 : 50} />
            </Box>
          ) : viewMode === "bar" ? (
            <Box
              height="100%"
              bgcolor={theme.palette.action.hover}
              borderRadius={2}
              p={2}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.action.hover} 100%)`,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  data={chartData}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis type="number" />
                  <YAxis
                    type="category"
                    dataKey="product"
                    width={isMobile ? 80 : 120}
                    tick={{ fontSize: isMobile ? 12 : 14 }}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
                  />
                  <Legend />
                  <ReferenceLine x={0} stroke={theme.palette.text.secondary} />
                  <Bar
                    dataKey="lastRate"
                    name="Last Rate"
                    fill={CONFIG.chart.colors.lastRate}
                    animationDuration={500}
                  />
                  <Bar
                    dataKey="currentRate"
                    name="Current Rate"
                    fill={CONFIG.chart.colors.currentRate}
                    animationDuration={500}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Stock Item Name</TableCell>
                    <TableCell align="right">Last Rate</TableCell>
                    <TableCell align="right">Current Rate</TableCell>
                    <TableCell align="right">Variance (Rs.)</TableCell>
                    <TableCell align="right">Variance (%)</TableCell>
                    <TableCell align="right">Month</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chartData.map((item, index) => {
                    const isPositiveVariance = (item.variance || 0) >= 0;

                    return (
                      <TableRow key={item.id || index}>
                        <TableCell>{item.product || "N/A"}</TableCell>
                        <TableCell align="right">
                          Rs. {(item.lastRate || 0).toFixed(2)}
                        </TableCell>
                        <TableCell align="right">
                          Rs. {(item.currentRate || 0).toFixed(2)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: isPositiveVariance
                              ? colors.redAccent[500]
                              : colors.greenAccent[500],
                            fontWeight: 600,
                          }}
                        >
                          Rs. {(item.variance || 0).toFixed(2)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: isPositiveVariance
                              ? colors.redAccent[500]
                              : colors.greenAccent[500],
                            fontWeight: 600,
                          }}
                        >
                          {(item.variancePercentage || 0).toFixed(1)}%
                        </TableCell>
                        <TableCell align="right">
                          {item.month || "N/A"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default PurchasePriceVariance;